import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  PhoneCall,
  Lock,
  Mail,
  Loader2,
  AlertCircle,
  LogOut,
} from "lucide-react";
import { useVoip } from "./VoipContext";

export const VoipLoginModal: React.FC = () => {
  const {
    isLoginModalOpen,
    closeLoginModal,
    login,
    isConnected,
    ramal,
    logout,
  } = useVoip();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Rate limit no frontend (5 tentativas por minuto)
  const [attemptCount, setAttemptCount] = useState(0);
  const [blockCooldown, setBlockCooldown] = useState(0);

  useEffect(() => {
    let timer: any;
    if (blockCooldown > 0) {
      timer = setInterval(() => {
        setBlockCooldown((prev) => {
          if (prev <= 1) {
            setAttemptCount(0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [blockCooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (blockCooldown > 0) {
      setErrorMessage(
        `Limite de tentativas atingido. Aguarde ${blockCooldown}s.`,
      );
      return;
    }

    if (!email.trim() || !password) {
      setErrorMessage("E-mail ou senha inválidos");
      return;
    }

    setIsSubmitting(true);
    const newCount = attemptCount + 1;
    setAttemptCount(newCount);

    if (newCount >= 5) {
      setBlockCooldown(60);
    }

    try {
      const res = await login(email.trim(), password);
      if (!res.ok) {
        setErrorMessage(res.message || "E-mail ou senha inválidos");
      } else {
        setPassword("");
        setAttemptCount(0);
      }
    } catch {
      setErrorMessage("Erro de conexão, tente novamente");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isLoginModalOpen}
      onOpenChange={(open) => !open && closeLoginModal()}
    >
      <DialogContent
        className="sm:max-w-md border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-2">
            <PhoneCall className="w-6 h-6" />
          </div>
          <DialogTitle className="text-xl font-bold">
            {isConnected
              ? "Telefonia VoIP Conectada"
              : "Conectar Telefonia VoIP"}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm">
            {isConnected
              ? `Sua conta API4COM está ativa no ramal ${ramal}. Você pode realizar chamadas clicando nos telefones dos leads.`
              : "Insira suas credenciais da API4COM para habilitar chamadas via Click-to-Call direto no CRM."}
          </DialogDescription>
        </DialogHeader>

        {isConnected ? (
          <div className="py-4 space-y-4">
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center gap-3 text-sm text-emerald-700 dark:text-emerald-400">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              <span>
                Ramal Ativo: <strong>{ramal}</strong>
              </span>
            </div>
            <DialogFooter className="flex justify-between sm:justify-between w-full">
              <Button variant="outline" onClick={closeLoginModal}>
                Fechar
              </Button>
              <Button
                variant="destructive"
                className="gap-2"
                onClick={async () => {
                  await logout();
                  closeLoginModal();
                }}
              >
                <LogOut className="w-4 h-4" />
                <span>Sair do VoIP (Logout)</span>
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            {errorMessage && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-lg flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label
                htmlFor="voip-email"
                className="text-xs font-semibold uppercase text-muted-foreground"
              >
                E-mail API4COM
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="voip-email"
                  type="email"
                  placeholder="seu.email@empresa.com.br"
                  className="pl-9"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting || blockCooldown > 0}
                  autoComplete="username"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="voip-pass"
                className="text-xs font-semibold uppercase text-muted-foreground"
              >
                Senha
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="voip-pass"
                  type="password"
                  placeholder="••••••••"
                  className="pl-9"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isSubmitting || blockCooldown > 0}
                  autoComplete="current-password"
                  required
                />
              </div>
            </div>

            <div className="text-[11px] text-muted-foreground">
              * Suas credenciais são enviadas com criptografia de ponta a ponta
              e a sessão é protegida com cookie httpOnly.
            </div>

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={closeLoginModal}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || blockCooldown > 0}
                className="gap-2 min-w-[110px]"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Entrando...</span>
                  </>
                ) : blockCooldown > 0 ? (
                  <span>Aguarde {blockCooldown}s</span>
                ) : (
                  <span>Entrar</span>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
